import { useState, useEffect, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';

//Redux
import { StateModel, actions } from 'store/store';
import { useSelector, useDispatch } from 'react-redux';

//Components
import Checkbox from 'components/checkbox/Checkbox';
import Nav from 'components/nav/Nav';
import Input from 'components/input/Input';
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import Capacity from './Capacity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-duotone-svg-icons';

//Styles
import './capacity.scss';
import './mediaQueries.scss';
import { validateEmail } from 'shared/shared.utilities';
import clsx from 'clsx';



const Customer = () => {
    const dispatch = useDispatch();
    const search = useLocation().search;
    const [step, setStep] = useState(1);
    const [customerName, setCustomerName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [mpan, setMpan] = useState("");
    const [mpanTopline, setMpanTopline] = useState("");
    const [importCapacity, setImportCapacity] = useState(null);
    const [preCovid,] = useState(false);
    const [openPrompt, setOpenPrompt] = useState(false);
    const [consent, setConsent] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const mp = new URLSearchParams(search).get('mpan');

    //Selectors
    const loading = useSelector((state: StateModel) => state.capacityState.fetchingCapacity);
    const capacity = useSelector((state: StateModel) => state.capacityState.capacity);
    const errorMessage = useSelector((state: StateModel) => state.capacityState.capacityErrorMessage);


    const clearForm = () => {
        setStep(1);
        setOpenPrompt(false);
        setCompanyName("");
        setCustomerName("");
        setContactEmail("");
        setMpan("");
        setMpanTopline("");
        setImportCapacity("");
        setPhoneNumber("");
        dispatch(actions.capacity.getCapacityFailure(""));
    }

    const getCapacity = (covidStatus: boolean) => {
        dispatch(actions.capacity.getPublicCapacityAsync(
            mpan,
            mpanTopline,
            importCapacity,
            phoneNumber,
            companyName,
            customerName,
            contactEmail,
            covidStatus
        ));
    }

    const next = (currentStep: number) => {
        if (currentStep === 4) {
            getCapacity(preCovid);
        } else {
            setStep(currentStep);
        }
    }

    useEffect(() => {
        if (step === 1) {
            setDisabled(!customerName || !companyName || !phoneNumber || validateEmail(contactEmail).error);
        } else if (step === 2) {
            setDisabled(!importCapacity || !mpanTopline || mpanTopline.length < 8);
        } else if (step === 3) {
            setDisabled(!consent);
        }
    }, [step, customerName, companyName, phoneNumber, contactEmail, mpanTopline, importCapacity, consent]);

    useEffect(() => {
        if (mp) {
            setMpan(mp);
            setOpenPrompt(true);
        }
    }, [mp]);


    return (
        <div className="capacity">
            <div className="public">
                <Nav public />
                {!capacity && (
                    <div className="public-content">
                        <Modal 
                            open={openPrompt}
                            component={
                                <div className="onboard-modal-content">
                                    <div className="dots">
                                        {[1,2,3].map((dot, index) => (
                                            <div className={clsx("dot", index + 1 === step && "active")} key={index}></div>
                                        ))}
                                    </div>

                                    {step === 1 && (
                                        <Fragment>
                                            <span className="title">Your details</span>
                                            <Input 
                                                inputLabel="Your Name"
                                                inputValue={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                            />
                                            <Input 
                                                inputLabel="Company Name"
                                                inputValue={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                            />
                                            <Input 
                                                inputLabel="Contact Number"
                                                inputPlaceholder=""
                                                inputValue={phoneNumber}
                                                onChange={(e) => {
                                                    const onlyNums = e.target.value.replace(/[^0-9]/g, '');

                                                    if (onlyNums.length < 11) {
                                                        setPhoneNumber(onlyNums);
                                                    } else if (onlyNums.length === 11) {
                                                        const number = onlyNums.replace(
                                                            /(\d{4})(\d{4})(\d{3})/,
                                                            '($1) $2-$3'
                                                        );

                                                        setPhoneNumber(number);
                                                    }
                                                }}
                                            />
                                            <Input 
                                                inputLabel="Contact Email"
                                                inputValue={contactEmail}
                                                onChange={(e) => setContactEmail(e.target.value)}
                                            />
                                        </Fragment>
                                    )}

                                    {step === 2 && (
                                        <Fragment>
                                            <span className="title">Meter details</span>
                                            <Input 
                                                inputLabel="Current Topline"
                                                maxLength={8}
                                                inputValue={mpanTopline}
                                                onChange={(e) => {
                                                    setMpanTopline(e.target.value);
                                                }}
                                            />
                                            <Input 
                                                isNumber
                                                inputLabel="Import Capacity (kVA)"
                                                inputValue={importCapacity}
                                                onChange={(e) => setImportCapacity(e.target.value)}
                                            />
                                        </Fragment>
                                    )}

                                    {step === 3 && (
                                        <Fragment>
                                            <span className="title">Authorisation</span>
                                            <p>In order to analyse your usage, we must gather your industry-held metering and consumption data.</p>
                                            <p>By agreeing to proceed, you are confirming that you, <span>{customerName}</span>, on behalf of <span>{companyName}</span>, are authorising Northern Gas and Power to access your industry held metering and consumption data for the meter number <span>{mpan}</span>.</p>
                                            <p style={{ marginTop: "40px" }}>From time to time, we may wish to contact you with marketing information and updates about our products and services by email or telephone.</p>
                                            <Checkbox 
                                                text="By agreeing to proceed, I consent to receiving marketing information from Northern Gas and Power."
                                                isActive={consent}
                                                toggleCheckbox={() => setConsent(!consent)}
                                            />
                                        </Fragment>
                                    )}

                                    {!_.isEmpty(errorMessage) && (
                                        <div className="error">
                                            {errorMessage}
                                        </div>
                                    )}

                                    <div className="actions">
                                        <Button 
                                            text="previous"
                                            disabled={step === 1}
                                            onClick={() => setStep(step - 1)} 
                                        />
                                        <Button 
                                            text={step === 3 ? "submit" : "next"}
                                            isLoading={step === 3 && loading}
                                            loadingText="fetching-analysis"
                                            disabled={disabled}
                                            onClick={() => next(step + 1)} 
                                        />
                                    </div>
                                </div>
                            }
                            closeModal={() => {
                                setOpenPrompt(false);
                                setStep(1);
                                clearForm();
                            }}
                        />

                        <div className="details">
                            <span className="title">TCR Calculator</span>
                            <p>The Targeted Charging Review (TCR) is completely changing the way network charges are passed onto customers. These changes come into effect from April 2022.</p>
                            <p>Use our new calculator to find out how you will be affected.</p>
                            <div className="snippet">
                                <FontAwesomeIcon className="snippet-icon" icon={faCheckCircle} />
                                <span><span>Quick</span> - Get results within seconds</span>
                            </div>
                            <div className="snippet">
                                <FontAwesomeIcon className="snippet-icon" icon={faCheckCircle} />
                                <span><span>Accurate</span> - Based on your actual metering data</span>
                            </div>
                            <div className="snippet">
                                <FontAwesomeIcon className="snippet-icon" icon={faCheckCircle} />
                                <span><span>Free</span> - Our calculator is completely free to use</span>
                            </div>
                        </div>

                        <div className="onboard">
                            <span className="title">Get Started!</span>
                            <div className="start-input">
                                <Input 
                                    inputLabel=""
                                    inputPlaceholder="Enter meter number"
                                    maxLength={13}
                                    inputValue={mpan}
                                    onChange={(e) => setMpan(e.target.value)}
                                />
                                <Button 
                                    text="Go"
                                    disabled={!mpan || mpan.length < 13}
                                    onClick={() => setOpenPrompt(true)}
                                />
                            </div>
                        </div>

                    </div>
                )}
            </div>
            <div className="content" style={{ marginTop: "70px" }}>
                {capacity && (
                    <Capacity 
                        public
                        companyName={companyName}
                        customerName={customerName}
                        email={contactEmail}
                        phoneNumber={phoneNumber}
                        mpan={mpan}
                        clearForm={clearForm}
                        getCapacity={(preCovid) => getCapacity(preCovid)}
                    />
                )}
            </div>
        </div>
    )
}




export default Customer;